import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCapability,
  DisplayOptionCapability,
  CapabilitySet,
} from '../hooks/useCapability'
import * as displayOptionService from '../services/display-option.service'
import type {
  CommitOptionImageResult,
  DisplayOptionDto,
} from '../services/display-option.service'
import { OptionList } from './OptionList'
import { OptionDetail, type OptionDetailAction } from './OptionDetail'
import { AffectedInstancesConfirm } from './AffectedInstancesConfirm'
import { AddDisplayModeDialog } from './AddDisplayModeDialog'
import { registerSimulationV3Translations } from '../i18n/translations'
import './display-options-card.css'
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { alertService } = require('../../../_services/alert.service')

// Bug (2026-10-16) — register this module's own translations HERE, not just
// from `SimulationScreen.tsx`. This card is dual-host (AD-11, see the class
// doc below): the masterdata "Add/Edit product" screen
// (`components/masterdata/products/AddNew/addnew.js`) renders it directly
// and never loads the v3 simulation screen, so a user who edits the catalog
// without having opened a simulation this session had none of the
// `simulationV3.displayOptions.*` keys registered — every `t()` call with an
// inline `defaultValue` silently rendered that English default instead of
// the Hebrew string, while OTHER labels on the same screen (sourced from the
// always-loaded legacy bundle) translated fine. `translations.ts` only
// imports `i18next`, so this adds no part of the v3 Redux/canvas chunk.
//
// MODULE SCOPE on purpose, not a `useEffect`: registration must precede
// first paint. The call is idempotent, so the v3 screen registering the same
// keys later (or earlier) is a no-op.
registerSimulationV3Translations()

/**
 * feat-004 story 2.4 (AD-11) — the dual-host master-detail catalog editor.
 * Props-injected, host-agnostic: NO dependency on the v3 module's Redux
 * store (`simulationV3.slice.ts`) or the app's global root store beyond
 * what the injected `service` needs (defaults to `display-option.service.ts`,
 * itself built on the legacy `submitSets`/`submitCollection` pair).
 *
 * Does NOT wire into the masterdata tab (story 2.5's own job). Story 4.9
 * (Task 3) finally implements AD-11's "in-simulator mode also dispatches a
 * catalog-update action" — deferred since story 2.4/3.3 because nothing
 * hosted this component yet: `onCatalogUpdated` fires with the FULL,
 * freshly-merged options list after any successful CRUD/image mutation
 * (not just `setDefaultOption` — every catalog write can change capacity/
 * REC numbers already-placed items feed into), and the v3 host
 * (`SimulationCanvas`) is the one that knows how to turn that into a
 * `catalogUpdated` dispatch + calculate re-fire
 * (`store/simulationV3.thunks.ts:refreshCatalogAndRecalculate`) — this
 * component itself still has no store dependency.
 */
export interface DisplayOptionsCardProps {
  productId: number
  baseImageUrl: string
  /** Reported 2026-09-10: the UX spec's own wireframe puts the product's
   *  name + barcode as a header on this card ("Milk 1L — barcode 729000123")
   *  — the shipped card never had it (no host ever passed the product's
   *  identity down). Optional: a host that doesn't have it handy simply
   *  omits it and the header doesn't render, same as before. */
  productName?: string
  barcode?: string
  /** Reported 2026-09-09: an orientation's dims are always "— (calculated)"
   *  because the orientation DTO itself never stores real width/height/depth
   *  (only a display mode does) — passed through to OptionDetail so it can
   *  derive the real value via the product's base dims. Optional: a host
   *  that doesn't have it handy keeps the old "—" text. */
  baseDims?: { width: number; height: number; depth: number; uom?: string | null }
  capabilities?: CapabilitySet
  service?: typeof displayOptionService
  isRTL?: boolean
  dmode?: boolean
  /** Story 4.9 (AC4, Task 3) — fires with the full options list after any
   *  successful save/setDefault/enable-disable/archive/delete/image-upload
   *  mutation. Optional: a host that doesn't need it (masterdata tab)
   *  simply never passes it. */
  /**
   * `savedOptionUuid` (fix-035) — the option that was just written through
   * `updateOption`, and ONLY that. The server mirrors a FRONT row onto the
   * product record in the same transaction (fix-032), so a host holding
   * simulation state uses this to know the product's own hangable/stackable
   * flags have moved too. Absent for every other catalog write
   * (`setDefault`, `enable`/`disable`, `archive`, image upload, create) —
   * none of those touch the product.
   */
  onCatalogUpdated?: (
    options: DisplayOptionDto[],
    savedOptionUuid?: string
  ) => void
}

/**
 * Shapes of the loading skeleton (bug 1, 2026-10-08) — six orientation rows,
 * the display-modes section title and the "+ Add display mode" button, which
 * is what every product's list actually holds (orientations are always the
 * fixed six; see `OptionList`). Module-level constants so the placeholder
 * rows keep their identity across re-renders.
 */
const SKELETON_LIST_ROWS = [1, 2, 3, 4, 5, 6]
const SKELETON_DETAIL_FIELDS = [1, 2, 3, 4, 5]

export const DisplayOptionsCard: React.FC<DisplayOptionsCardProps> = ({
  productId,
  baseImageUrl,
  productName,
  barcode,
  baseDims,
  capabilities,
  service = displayOptionService,
  isRTL,
  dmode,
  onCatalogUpdated,
}) => {
  const { t } = useTranslation()
  const [options, setOptions] = useState<DisplayOptionDto[]>([])
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const [affectedConfirm, setAffectedConfirm] = useState<{
    count: number
    name: string
  } | null>(null)
  const [addModeOpen, setAddModeOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const isMounted = useRef(true)
  useEffect(
    () => () => {
      isMounted.current = false
    },
    []
  )
  // Set synchronously (not via setState) right before an AffectedInstances
  // disclosure is shown, so the immediately-following handleChanged call
  // (same save, same tick) can tell "this action already had its one signal"
  // and skip the separate "Saved" toast — one toast per action, ever.
  const justDisclosedRef = useRef(false)

  const canEdit = useCapability(DisplayOptionCapability.Edit, capabilities)
  const canCreate = useCapability(DisplayOptionCapability.Create, capabilities)
  const canUploadImage = useCapability(
    DisplayOptionCapability.ImageUpload,
    capabilities
  )

  // Review fix (2026-08-17): a ref mirror of `options`, always updated in
  // the SAME synchronous call that updates state. `options` itself (a
  // render-scope closure variable) can go stale if two mutations resolve
  // close together with no render in between — `handleChanged` and
  // `handleImageUploaded` used to read `options` directly to build their
  // merge, which could silently drop one update's contribution to the
  // other's. Route every `options` write through this helper so the ref
  // (and therefore every merge) is always current.
  const optionsRef = useRef<DisplayOptionDto[]>([])
  const updateOptions = useCallback(
    (updater: (prev: DisplayOptionDto[]) => DisplayOptionDto[]) => {
      const next = updater(optionsRef.current)
      optionsRef.current = next
      setOptions(next)
      return next
    },
    []
  )

  // story 2.6: `listOptions`/`updateOption`/etc. (every CRUD entry EXCEPT the
  // image commit) return raw `DisplayOptionDto`s with `hasDedicatedImage`/
  // `imageUrl` undefined (story 2.1 — no image columns on the CRUD DTO at
  // all, see display-option.service.ts Dev Notes). Carry the last-known
  // value forward from the PREVIOUS in-memory state whenever a fresh row
  // doesn't say otherwise, so a background refetch (or an unrelated save)
  // never regresses an already-known dedicated image back to "unknown".
  const withCarriedImageFields = useCallback(
    (fresh: DisplayOptionDto[], previous: DisplayOptionDto[]) => {
      const previousByUuid = new Map(previous.map((o) => [o.uuid, o]))
      return fresh.map((o) => {
        const prior = previousByUuid.get(o.uuid)
        return {
          ...o,
          hasDedicatedImage: o.hasDedicatedImage ?? prior?.hasDedicatedImage,
          imageUrl: o.imageUrl ?? prior?.imageUrl,
        }
      })
    },
    []
  )

  const refetch = useCallback(
    async (opts?: { background?: boolean }) => {
      if (!opts?.background) setLoading(true)
      try {
        const list = await service.listOptions(productId)
        if (!isMounted.current) return
        updateOptions((prev) => withCarriedImageFields(list, prev))
        setSelectedUuid((current) => {
          if (current && list.some((o) => o.uuid === current)) return current
          return list.find((o) => o.isDefault)?.uuid ?? list[0]?.uuid ?? null
        })
      } catch (err) {
        if (isMounted.current)
          alertService.error(err instanceof Error ? err.message : String(err))
      } finally {
        if (isMounted.current && !opts?.background) setLoading(false)
      }
    },
    [productId, service, withCarriedImageFields, updateOptions]
  )

  useEffect(() => {
    refetch()
  }, [refetch])

  const handleChanged = (
    updated: DisplayOptionDto,
    action?: OptionDetailAction
  ) => {
    if (justDisclosedRef.current) {
      justDisclosedRef.current = false
    } else {
      alertService.success(
        t('simulationV3.displayOptions.detail.saved', { defaultValue: 'Saved' })
      )
    }
    const merged = updateOptions((prev) =>
      prev.map((o) =>
        o.uuid === updated.uuid
          ? // story 2.6: `updated` (from updateOption/enableOption/etc.) never
            // carries image fields either — carry the prior value forward the
            // same way `withCarriedImageFields` does for a full refetch.
            {
              ...updated,
              hasDedicatedImage: updated.hasDedicatedImage ?? o.hasDedicatedImage,
              imageUrl: updated.imageUrl ?? o.imageUrl,
            }
          : // Review fix (2026-08-17): the server enforces a single default
            // per product, but `updated` alone doesn't say which OTHER row
            // lost that status on a `setDefaultOption` call — its own
            // `SetDefaultResult.previousDefault` is discarded upstream in
            // `OptionDetail.handleSetDefault`. Clear `isDefault` on every
            // other row locally whenever the merged-in row IS the new
            // default, so neither the merged list nor
            // `onCatalogUpdated`/Redux ever carries two `isDefault: true`
            // rows (`withDefaultOptionWidths`'s `.find(isDefault)` is
            // first-match-wins).
            updated.isDefault && o.isDefault
              ? { ...o, isDefault: false }
              : o
      )
    )
    // Story 4.9 (AC4, Task 3) — the in-simulator host re-fires REC/NOW off
    // this SAME merged list, not the background refetch below (that one
    // is fire-and-forget and may resolve after the caller has moved on).
    // fix-035: only a SAVE went through `updateOption`, the one call that
    // also mirrors a FRONT row onto the product record. Every other action
    // here writes isDefault/isEnabled/archivedAt and leaves the product be.
    onCatalogUpdated?.(merged, action === 'save' ? updated.uuid : undefined)
    // AC4: saves commit immediately via CRUD — a BACKGROUND refetch keeps
    // the list (e.g. default-star moves, enable/disable membership) in sync
    // with the server, never buffered into any planogram/simulation state.
    // Background (not the initial-mount fetch) so a save doesn't blank the
    // whole list+detail pane back to a loading skeleton on every edit.
    refetch({ background: true })
  }

  // story 2.6 (AC2): fires from OptionImageUpload -> OptionDetail on a
  // successful commit. Merges the AUTHORITATIVE server response straight
  // into `options` state (never a stale local/base64 preview) and forces the
  // same background refetch every other mutation uses, for list-wide sync.
  const handleImageUploaded = (
    uuid: string,
    result: CommitOptionImageResult
  ) => {
    // 2026-08-14 review fix: mirror `refetch`'s own isMounted guard — this
    // fires from an async upload callback that can resolve after the card
    // unmounts (e.g. the masterdata host's Tab switches away mid-upload).
    if (!isMounted.current) return
    const merged = updateOptions((prev) =>
      prev.map((o) =>
        o.uuid === uuid
          ? {
              ...o,
              hasDedicatedImage: result.hasDedicatedImage,
              imageUrl: result.imageUrl,
            }
          : o
      )
    )
    onCatalogUpdated?.(merged)
    refetch({ background: true })
  }

  const handleError = (message: string) => alertService.error(message)

  // "+ Add display mode" (closes the deferred story 2.4 gap — createDisplayMode
  // was implemented/tested in the service layer but never wired to any control).
  // Mirrors handleChanged's merge/select/notify/background-refetch shape.
  const handleModeCreated = (created: DisplayOptionDto) => {
    alertService.success(
      t('simulationV3.displayOptions.dialog.addMode.created', {
        defaultValue: 'Display mode created',
      })
    )
    const merged = updateOptions((prev) => [...prev, created])
    setSelectedUuid(created.uuid)
    setAddModeOpen(false)
    onCatalogUpdated?.(merged)
    refetch({ background: true })
  }

  const selectedOption = options.find((o) => o.uuid === selectedUuid) ?? null

  return (
    <div className="simv3-doc" data-testid="display-options-card">
      {productName ? (
        <div className="simv3-doc__header" data-testid="display-options-header">
          {barcode ? (
            // Reported 2026-09-10: rendered via t()'s interpolation, a
            // product name containing a literal `"` (e.g. Hebrew ס״מ) came
            // back HTML-entity-escaped ("&quot;") — i18next's interpolator
            // escapes by default and this app's i18n.js never overrides
            // that, so the escaped entity showed up LITERALLY in this plain
            // text (never HTML-parsed) context. That stray neutral/LTR run
            // sitting inside an RTL name is also what was scrambling the
            // visual word order. Fix: never pass productName/barcode
            // THROUGH t()'s interpolation — render them as plain JSX
            // children (React never escapes/re-encodes text nodes), and
            // only translate the static word "barcode" itself.
            <>
              {productName}{' — '}
              {t('simulationV3.displayOptions.header.barcodeLabel', {
                defaultValue: 'barcode',
              })}{' '}
              {barcode}
            </>
          ) : (
            productName
          )}
        </div>
      ) : null}
      {canEdit ? (
        <div className="simv3-doc__banner" data-testid="catalog-edit-banner">
          {t('simulationV3.displayOptions.banner', {
            defaultValue:
              "You're editing the catalog — changes apply immediately to every planogram.",
          })}
        </div>
      ) : null}

      {loading ? (
        /**
         * Reported 2026-10-08 (bug 1): the popup "does not render completely
         * at once" — it opened as a thin strip holding the header, the banner
         * and a bare one-line "Loading…", then jumped to full size when
         * `listOptions` resolved.
         *
         * The cause was that one-line span: nothing reserved the master-detail
         * row's footprint while the catalog was in flight, and both hosts size
         * to content (`.simv3-doc-modal-box` fixes WIDTH only; the masterdata/
         * ProductDetailsModal host lets `.pdm-body` grow), so the box was as
         * short as its own placeholder. A skeleton built from the SAME
         * `.simv3-doc__body` / `__list` / `__detail` elements the loaded state
         * uses (plus the `min-height` floor they now share, see
         * display-options-card.css) means the popup is laid out once, at its
         * real size, and only the content inside it swaps in — no progressive
         * expansion.
         *
         * `aria-hidden` on the placeholder scaffolding and ONE live region
         * carrying the real "Loading…" text: a screen reader hears the status,
         * not two panes of fake rows. The `display-options-card-loading`
         * testid moves to the root here so existing "is it still loading?"
         * queries keep working.
         */
        <div
          className="simv3-doc__body simv3-doc__body--loading"
          data-testid="display-options-card-loading"
          role="status"
          aria-busy="true"
        >
          <span className="simv3-doc__sr-only">
            {t('simulationV3.displayOptions.loading', {
              defaultValue: 'Loading…',
            })}
          </span>
          <div className="simv3-doc__list" aria-hidden="true">
            {SKELETON_LIST_ROWS.map((row) => (
              <div className="simv3-doc__row" key={row}>
                <span className="simv3-doc__skeleton simv3-doc__skeleton--icon" />
                <span className="simv3-doc__skeleton simv3-doc__skeleton--label" />
              </div>
            ))}
            <div className="simv3-doc__section-title">
              <span className="simv3-doc__skeleton simv3-doc__skeleton--title" />
            </div>
            <span className="simv3-doc__skeleton simv3-doc__skeleton--button" />
          </div>
          <div className="simv3-doc__detail" aria-hidden="true">
            <span className="simv3-doc__skeleton simv3-doc__skeleton--title" />
            {SKELETON_DETAIL_FIELDS.map((field) => (
              <div className="simv3-doc__field" key={field}>
                <span className="simv3-doc__skeleton simv3-doc__skeleton--field-label" />
                <span className="simv3-doc__skeleton simv3-doc__skeleton--field-value" />
              </div>
            ))}
            <div className="simv3-doc__actions">
              <span className="simv3-doc__skeleton simv3-doc__skeleton--button" />
              <span className="simv3-doc__skeleton simv3-doc__skeleton--button" />
            </div>
          </div>
        </div>
      ) : (
        // story 2.5 AC3 review fix (2026-08-14): a real wrapper element, not
        // a fragment — the container query that collapses this row to
        // stacked layout needs a DESCENDANT of `.simv3-doc` to restyle (see
        // display-options-card.css's note on why `.simv3-doc` itself can't
        // be the query's own target).
        <div className="simv3-doc__body">
          <OptionList
            options={options}
            selectedUuid={selectedUuid}
            onSelect={setSelectedUuid}
            baseImageUrl={baseImageUrl}
            readOnly={!canEdit}
            onAddMode={canCreate ? () => setAddModeOpen(true) : undefined}
            // Threaded so each row's isometric box carries that option's own
            // proportions, the same dims the detail pane already receives
            // (PLAN-TC-1376/1377). A host that passes no baseDims leaves
            // every box the plain cube, as before.
            baseDims={
              baseDims
                ? {
                    width: baseDims.width,
                    height: baseDims.height,
                    depth: baseDims.depth,
                  }
                : undefined
            }
          />
          <OptionDetail
            option={selectedOption}
            productId={productId}
            baseImageUrl={baseImageUrl}
            baseDims={baseDims}
            readOnly={!canEdit}
            service={service}
            onChanged={handleChanged}
            onLiveInstanceCount={(count, name) => {
              justDisclosedRef.current = true
              setAffectedConfirm({ count, name })
            }}
            onError={handleError}
            canUploadImage={canUploadImage}
            onImageUploaded={handleImageUploaded}
          />
        </div>
      )}

      {affectedConfirm ? (
        <AffectedInstancesConfirm
          visible
          optionName={affectedConfirm.name}
          liveInstanceCount={affectedConfirm.count}
          onClose={() => setAffectedConfirm(null)}
          isRTL={isRTL}
          dmode={dmode}
        />
      ) : null}

      <AddDisplayModeDialog
        visible={addModeOpen}
        productId={productId}
        service={service}
        onCreated={handleModeCreated}
        onClose={() => setAddModeOpen(false)}
        onError={handleError}
        isRTL={isRTL}
        dmode={dmode}
      />
    </div>
  )
}
